import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { emailDeliveryApi } from "../../services/emailDeliveryApi";
import { usePermissions } from "../../hooks/usePermissions";
import { StatusBadge } from "../../components/StatusBadge";
import { PageHelpBanner } from "../../components/PageHelpBanner";
import { formatApiErrorDetail } from "../../lib/api";
import { useLocalization } from "../../hooks/useLocalization";

export default function EmailDeliveryPage() {
  const { t } = useLocalization();
  const { canManageEmailDelivery } = usePermissions();
  const [flags, setFlags] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [deliveries, setDeliveries] = useState([]);
  const [suppressions, setSuppressions] = useState([]);

  const load = useCallback(() => {
    emailDeliveryApi.featureFlags().then((r) => setFlags(r.data)).catch(() => {});
    emailDeliveryApi.listTemplates().then((r) => setTemplates(r.data)).catch(() => {});
    emailDeliveryApi.listDeliveries().then((r) => setDeliveries(r.data)).catch(() => {});
    emailDeliveryApi.listSuppressions().then((r) => setSuppressions(r.data)).catch(() => {});
  }, []);

  useEffect(() => { if (canManageEmailDelivery) load(); }, [canManageEmailDelivery, load]);

  if (!canManageEmailDelivery) return <p className="text-sm text-gray-500" data-testid="email-access-denied">{t("common.accessDenied")}</p>;

  const resend = async (id) => {
    try { await emailDeliveryApi.resend(id); toast.success(t("mgmtEmail.resendAttempted")); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-5" data-testid="email-delivery-page">
      <PageHelpBanner pageKey="email-delivery" title={t("pageHelp.defaultTitle")}>
        <p dangerouslySetInnerHTML={{ __html: t("mgmtEmail.help") }} />
      </PageHelpBanner>
      <div>
        <h2 className="text-2xl font-bold tracking-tight text-gray-900">{t("mgmtEmail.title")}</h2>
        <p className="text-sm text-gray-500" data-testid="email-enabled-flag">
          EMAIL_ENABLED {t("mgmtEmail.isText")} <span className="font-semibold">{flags?.email_enabled ? t("mgmtEmail.trueLive") : t("mgmtEmail.falseMockOnly")}</span>. {templates.length} {t("mgmtEmail.templatesPrepared")}
        </p>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 overflow-x-auto">
        <table className="w-full text-sm min-w-[640px]">
          <thead className="bg-gray-50 text-gray-500 text-left"><tr><th className="px-4 py-3 font-medium">{t("mgmtEmail.template")}</th><th className="px-4 py-3 font-medium">{t("mgmtEmail.recipient")}</th><th className="px-4 py-3 font-medium">{t("common.status")}</th><th className="px-4 py-3 font-medium">{t("mgmtEmail.attempts")}</th><th className="px-4 py-3 font-medium"></th></tr></thead>
          <tbody>
            {deliveries.map((d) => (
              <tr key={d.id} className="border-t border-gray-100" data-testid={`email-delivery-row-${d.id}`}>
                <td className="px-4 py-2 font-semibold text-gray-800">{d.template_key}</td>
                <td className="px-4 py-2 text-gray-600">{d.recipient_email}</td>
                <td className="px-4 py-2"><StatusBadge status={d.status} /></td>
                <td className="px-4 py-2 text-gray-500">{d.attempt_count}</td>
                <td className="px-4 py-2">
                  {d.status === "FAILED" && <button data-testid={`resend-email-btn-${d.id}`} onClick={() => resend(d.id)} className="text-xs font-semibold text-blue-600 hover:underline">{t("mgmtEmail.resend")}</button>}
                </td>
              </tr>
            ))}
            {deliveries.length === 0 && <tr><td colSpan={5} className="px-4 py-6 text-center text-gray-500" data-testid="no-email-deliveries-row">{t("mgmtEmail.noEmailsQueued")}</td></tr>}
          </tbody>
        </table>
      </div>

      <div>
        <h3 className="text-sm font-semibold text-gray-700 mb-2">{t("mgmtEmail.suppressionList")} ({suppressions.length})</h3>
        <div className="bg-white rounded-xl border border-gray-200 overflow-x-auto">
          <table className="w-full text-sm min-w-[640px]">
            <thead className="bg-gray-50 text-gray-500 text-left"><tr><th className="px-4 py-3 font-medium">{t("mgmtEmail.email")}</th><th className="px-4 py-3 font-medium">{t("mgmtEmail.reason")}</th></tr></thead>
            <tbody>
              {suppressions.map((s) => (
                <tr key={s.id} className="border-t border-gray-100" data-testid={`suppression-row-${s.id}`}>
                  <td className="px-4 py-2 text-gray-600">{s.email}</td>
                  <td className="px-4 py-2 text-gray-500">{s.reason}</td>
                </tr>
              ))}
              {suppressions.length === 0 && <tr><td colSpan={2} className="px-4 py-4 text-center text-gray-500" data-testid="no-suppressions-row">{t("mgmtEmail.noSuppressedRecipients")}</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
